import { logError, logInfo } from '@utils/logger';
import { calculateDistanceToSegment } from '../routeHelpers';

// Cache for structures data
let structuresCache = null;
let lastFetchTime = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const fetchStructuresData = async () => {
  try {
    // Check cache first
    if (structuresCache && lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)) {
      logInfo('Using cached structures data');
      return structuresCache;
    }

    // Fetch from Données Québec WFS service
    const response = await fetch(
      'https://ws.mapserver.transports.gouv.qc.ca/swtq?service=WFS&' +
      'version=2.0.0&request=GetFeature&' +
      'typename=ms:structures&' +
      'outputformat=geojson'
    );

    if (!response.ok) {
      throw new Error('Failed to fetch structures data');
    }

    const data = await response.json();
    
    // Transform and clean the data
    const structures = data.features.map(feature => ({
      id: feature.properties.ID_STRUCT || feature.id,
      type: feature.properties.TYPE_STRUC || 'unknown',
      name: feature.properties.NOM_STRUC || '',
      coordinates: {
        latitude: feature.geometry.coordinates[1],
        longitude: feature.geometry.coordinates[0]
      },
      properties: {
        clearance: feature.properties.HAUTEUR_LIB || null,
        width: feature.properties.LARGEUR_CH || null,
        length: feature.properties.LONGUEUR || null,
        yearBuilt: feature.properties.ANNEE_CONS || null,
        municipality: feature.properties.NOM_MUNIC || '',
        route: feature.properties.NO_ROUTE || '',
        condition: feature.properties.ETAT || ''
      }
    }));

    // Update cache
    structuresCache = structures;
    lastFetchTime = Date.now();
    
    logInfo(`Fetched ${structures.length} structures`);
    return structures;
  } catch (error) {
    logError('Failed to fetch structures:', error);
    throw error;
  }
};

export const filterStructuresAlongRoute = async (structures, routePoints, maxDistance, filters = {}) => {
  try {
    if (!structures?.length || !routePoints?.length) {
      return [];
    }

    // Filter structures by type if specified
    let filteredStructures = structures;
    if (filters.types?.length) {
      filteredStructures = structures.filter(s => filters.types.includes(s.type));
    }

    // Filter structures by clearance if specified
    if (filters.minClearance) {
      filteredStructures = filteredStructures.filter(s => {
        const clearance = parseFloat(s.properties.clearance);
        return !isNaN(clearance) && clearance >= filters.minClearance;
      });
    }

    // Find structures within buffer distance of route
    const nearbyStructures = filteredStructures.filter(structure => {
      // Check each route segment
      for (let i = 0; i < routePoints.length - 1; i++) {
        const distance = calculateDistanceToSegment(
          structure.coordinates,
          routePoints[i],
          routePoints[i + 1]
        );
        
        if (distance <= maxDistance) {
          // Add distance to structure object
          structure.distanceFromRoute = distance;
          // Add route progress (distance along route)
          structure.routeProgress = calculateRouteProgress(
            structure,
            routePoints,
            i
          );
          return true;
        }
      }
      return false;
    });

    // Sort by distance along route
    nearbyStructures.sort((a, b) => a.routeProgress - b.routeProgress);

    logInfo(`Found ${nearbyStructures.length} structures along route`);
    return nearbyStructures;
  } catch (error) {
    logError('Failed to filter structures:', error);
    throw error;
  }
};

const calculateRouteProgress = (structure, routePoints, segmentIndex) => {
  let distance = 0;
  
  // Add distance of completed segments
  for (let i = 0; i < segmentIndex; i++) {
    distance += calculateDistance(
      routePoints[i],
      routePoints[i + 1]
    );
  }
  
  // Add distance along current segment
  const projection = projectPointOnSegment(
    structure.coordinates,
    routePoints[segmentIndex],
    routePoints[segmentIndex + 1]
  );
  
  distance += calculateDistance(
    routePoints[segmentIndex],
    { latitude: projection.lat, longitude: projection.lng }
  );
  
  return distance;
};

const calculateDistance = (point1, point2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = point1.latitude * Math.PI/180;
  const φ2 = point2.latitude * Math.PI/180;
  const Δφ = (point2.latitude - point1.latitude) * Math.PI/180;
  const Δλ = (point2.longitude - point1.longitude) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
};

const projectPointOnSegment = (point, start, end) => {
  const lat = point.latitude;
  const lng = point.longitude;
  const x = lng;
  const y = lat;
  const x1 = start.longitude;
  const y1 = start.latitude;
  const x2 = end.longitude;
  const y2 = end.latitude;

  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  return { lat: yy, lng: xx };
};